<template>
  <div class="container-fluid py-4 executive-overview">
    <!-- Header -->
    <div class="mb-4">
      <h4 class="font-weight-bold mb-1">Executive Overview</h4>
      <p class="text-muted">
        Real-time business metrics and performance insights
      </p>
    </div>

    <!-- Filter Section -->
    <div class="card shadow-sm mb-4">
      <div
        class="card-body d-flex flex-wrap justify-content-between align-items-center"
      >
        <div class="btn-group flex-wrap mb-2 gap-2">
          <button
            v-for="option in filterOptions"
            :key="option.value"
            type="button"
            class="btn btn-sm"
            :class="
              selectedFilter === option.value
                ? 'btn-primary'
                : 'btn-outline-primary'
            "
            @click="selectFilter(option.value)"
          >
            {{ option.label }}
          </button>
          <div class="ml-3">
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="resetFilter"
            >
              <i class="fas fa-sync"></i> Reset
            </button>
          </div>
        </div>
      </div>

      <!-- Custom date picker -->
      <div v-if="selectedFilter === 'custom'" class="card-footer bg-light">
        <div class="d-flex flex-wrap d-flex flex-column gap-2">
          <b-form-datepicker
            v-model="filter.from"
            locale="en-CA"
            class="mb-2"
            placeholder="Start Date"
            style="width: 20%"
          />
          <b-form-datepicker
            v-model="filter.to"
            locale="en-CA"
            class="mb-2"
            placeholder="End Date"
            style="width: 20%"
          />
          <button
            v-if="filter.from !== null && filter.to !== null"
            class="btn btn-success btn-sm mr-3"
            @click="applyFilter"
            style="width: 20%"
          >
            <i class="fas fa-search"></i> Apply
          </button>
        </div>
      </div>
    </div>
    <div>
      <h5 class="font-weight-bold text-black text-uppercase mt-5">
        {{
          this?.filterOptions?.find(
            (data) => data?.value === this.selectedFilter
          )?.label
        }}
      </h5>
    </div>
    <!-- Financial Performance + Today’s Activity -->
    <div class="row mb-4">
      <DashboardCard
        title="Financial Performance"
        type="performance"
        :data="dashboard"
        :formatNumber="formatNumber"
      />

      <DashboardCard
        title="Activity"
        type="today"
        :data="dashboard"
        :formatNumber="formatNumber"
      />
    </div>

    <!-- 🎯 User Overview -->
    <div class="marginTopCard">
      <h6 class="text-muted font-weight-bold">User Overview</h6>
      <div class="row">
        <ModernCard
          title="Total Users"
          :value="`${dashboard.total_users}`"
          icon="fas fa-users"
          subtitle="Active User Base"
          subtitleIcon="fas fa-user-friends"
          subtitleClass="text-primary font-weight-medium"
        />
        <ModernCard
          title="Registered Today"
          :value="`${dashboard.today_registered_users}`"
          icon="fas fa-user-plus"
          subtitle="Daily Growth"
          subtitleIcon="fas fa-chart-line"
          subtitleClass="text-success font-weight-medium"
        />
        <ModernCard
          title="Users with Agent"
          :value="`${dashboard.users_with_agent}`"
          icon="fas fa-handshake"
          subtitle="Affiliate Users"
          subtitleIcon="fas fa-link"
          subtitleClass="text-info font-weight-medium"
        />
        <ModernCard
          title="Users Without Agent"
          :value="`${dashboard.users_no_agent}`"
          icon="fas fa-user-slash"
          subtitle="Independent Users"
          subtitleIcon="fas fa-user"
          subtitleClass="text-muted font-weight-medium"
        />
      </div>
    </div>

    <hr class="marginTopCard" />

    <!-- 💰 Financial Flow -->
    <div class="mt-4">
      <h6 class="text-muted font-weight-bold">Financial Flow</h6>
      <div class="row">
        <ModernCard
          title="Total Deposits"
          :value="`MMK ${this.formatNumber(dashboard.total_deposits)}`"
          icon="fas fa-wallet"
          subtitle="Incoming Funds"
          subtitleIcon="fas fa-arrow-down"
          subtitleClass="text-success font-weight-medium"
        />
        <ModernCard
          title="Total Withdrawals"
          :value="`MMK ${this.formatNumber(dashboard.total_withdrawals)}`"
          icon="fas fa-money-bill-wave"
          subtitle="Outgoing Funds"
          subtitleIcon="fas fa-arrow-up"
          subtitleClass="text-danger font-weight-medium"
        />
        <ModernCard
          title="Total Balance"
          :value="`MMK ${this.formatNumber(dashboard.total_balance)}`"
          icon="fas fa-piggy-bank"
          subtitle="System Liquidity"
          subtitleIcon="fas fa-balance-scale"
          subtitleClass="text-warning font-weight-medium"
        />
        <ModernCard
          title="Deposits"
          :value="`MMK ${this.formatNumber(dashboard.today_deposits)}`"
          icon="fas fa-wallet"
          subtitle="New Capital Inflow"
          subtitleIcon="fas fa-arrow-down"
          subtitleClass="text-success font-weight-medium"
        />
      </div>
    </div>
    <hr class="marginTopCard" />
    <!-- 🏦 Betting & Revenue -->
    <div>
      <h6 class="text-muted font-weight-bold">Betting & Revenue</h6>
      <div class="row">
        <ModernCard
          title="Bet Amount"
          :value="`MMK ${this.formatNumber(dashboard.today_bet_amount)}`"
          icon="fas fa-coins"
          subtitle="Daily Betting Volume"
          subtitleIcon="fas fa-chart-line"
          subtitleClass="text-success font-weight-medium"
        />
        <ModernCard
          title="Total Bet Amount"
          :value="`MMK ${this.formatNumber(dashboard.total_bet_amount)}`"
          icon="fas fa-dice"
          subtitle="Cumulative Betting Volume"
          subtitleIcon="fas fa-chart-bar"
          subtitleClass="text-info font-weight-medium"
        />
        <ModernCard
          title="Total Win/Lose (Company)"
          :value="`MMK ${this.formatNumber(dashboard.total_win_lose_company)}`"
          icon="fas fa-building"
          subtitle="Company Results"
          subtitleIcon="fas fa-arrow-up"
          subtitleClass="text-primary font-weight-medium"
        />
        <ModernCard
          title="Total Win/Lose (User)"
          :value="`MMK ${this.formatNumber(dashboard.total_win_lose_user)}`"
          icon="fas fa-user-tie"
          subtitle="User Results"
          subtitleIcon="fas fa-arrow-down"
          subtitleClass="text-warning font-weight-medium"
        />
      </div>
    </div>
    <hr class="marginTopCard" />

    <!-- 🤝 Commissions & Profit Sharing -->
    <div>
      <h6 class="text-muted font-weight-bold">Commissions & Profit Sharing</h6>
      <div class="row">
        <ModernCard
          title="Company Commission (Today)"
          :value="`MMK ${this.formatNumber(
            dashboard.today_company_commission
          )}`"
          icon="fas fa-briefcase"
          subtitle="House Earnings"
          subtitleIcon="fas fa-chart-line"
          subtitleClass="text-success font-weight-medium"
        />
        <ModernCard
          title="Master Commission (Today)"
          :value="`MMK ${this.formatNumber(dashboard.today_master_commission)}`"
          icon="fas fa-crown"
          subtitle="Top-Level Share"
          subtitleIcon="fas fa-user-shield"
          subtitleClass="text-info font-weight-medium"
        />
        <ModernCard
          title="Agent Commission (Today)"
          :value="`MMK ${this.formatNumber(dashboard.today_agent_commission)}`"
          icon="fas fa-user-tie"
          subtitle="Affiliate Revenue"
          subtitleIcon="fas fa-handshake"
          subtitleClass="text-success font-weight-medium"
        />
        <ModernCard
          title="Partner Profit"
          :value="`MMK ${this.formatNumber(dashboard.today_partner_profit)}`"
          icon="fas fa-users"
          subtitle="Joint Profit"
          subtitleIcon="fas fa-user-friends"
          subtitleClass="text-primary font-weight-medium"
        />
      </div>
    </div>
    <hr class="marginTopCard" />

    <!-- 📊 Total Cumulative Insights -->
    <div>
      <h6 class="text-muted font-weight-bold">Cumulative Insights</h6>
      <div class="row">
        <ModernCard
          title="Total Company Commission"
          :value="`MMK ${this.formatNumber(
            dashboard.total_company_commission
          )}`"
          icon="fas fa-building"
          subtitle="All-Time Revenue"
          subtitleIcon="fas fa-chart-bar"
          subtitleClass="text-success font-weight-medium"
        />
        <ModernCard
          title="Total Master Commission"
          :value="`MMK ${this.formatNumber(dashboard.total_master_commission)}`"
          icon="fas fa-crown"
          subtitle="Cumulative Share"
          subtitleIcon="fas fa-trophy"
          subtitleClass="text-warning font-weight-medium"
        />
        <ModernCard
          title="Total Agent Commission"
          :value="`MMK ${this.formatNumber(dashboard.total_agent_commission)}`"
          icon="fas fa-user-tie"
          subtitle="Affiliate Lifetime Earnings"
          subtitleIcon="fas fa-coins"
          subtitleClass="text-info font-weight-medium"
        />
        <ModernCard
          title="Total Profit (Company)"
          :value="`MMK ${this.formatNumber(dashboard.today_company_profit)}`"
          icon="fas fa-building"
          subtitle="Net Profit"
          subtitleIcon="fas fa-dollar-sign"
          subtitleClass="text-success font-weight-medium"
        />
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { mapState, mapActions } from 'vuex'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import DashboardHeader from '@/components/DashboardHeader.vue'
import ModernCard from '../components/card/ModernCard.vue'
import DashboardCard from "@/components/card/DashboardCard.vue";


export default {
  name: 'dashboard',
  metaInfo: {
    title: 'Home',
    titleTemplate: '%s ← BXB Dashboard',
  },
  components: {
    Loading,
    ModernCard,
    DashboardCard,
    DashboardHeader,
  },
  data() {
    return {
      isLoading: false,
      fullPage: true,
      dashboard: [],
      selectedFilter: 'today',
      filter: {
        from: null,
        to: null,
      },
      filterOptions: [
        // { label: 'All', value: 'all' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: '1 Week', value: 'oneweek' },
        { label: 'One Month', value: 'onemonth' },
        { label: 'Two Months', value: 'twomonths' },
        { label: 'Three Months', value: 'threemonths' },
        { label: 'Custom', value: 'custom' },
      ],
    }
  },
  computed: {
    ...mapState({}),
    selectedLabel() {
    const selected = this.filterOptions.find(opt => opt.value === this.selectedFilter);
    return selected ? selected.label : 'Select Filter';
  }
  },
  methods: {
    ...mapActions({
      dashboardAction: 'dashboardAction',
    }),
    formatNumber(number) {
    if (number == null || isNaN(number)) return "0";
    return new Intl.NumberFormat("en-US").format(number);
  },
    selectFilter(value) {
    this.selectedFilter = value;
    this.applyFilter();

  },


    async dashboardDetail(filter = {}) {
      try {
        this.isLoading = true
        const res = await this.dashboardAction(filter)

        if (res.success) {
          this.dashboard = res.data
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.isLoading = false
      }
    },

    async applyFilter() {

      const payload = {
        from: this.filter.from,
        to: this.filter.to,
        type: this.selectedFilter,
      }
      await this.dashboardDetail(payload)
    },

    async resetFilter() {
      this.selectedFilter = 'today'
      this.filter={
        from:null,
        to:null
      };
     const  payload = { from: null, to: null,type:"today" }
      await this.dashboardDetail(payload)
    },

    toOrder(status) {
      this.$router
        .replace({
          path: 'order-list',
          query: {
            page: 1,
            orderBy: 'desc',
            sortKey: 'created_at',
            limit: 10,
            fromDate: '',
            toDate: '',
            status: status,
            userId: '',
            email: '',
            orderNo: '',
          },
        })
        .catch(() => {})
    },
  },
  async mounted() {
    this.isLoading = true
    this.resetFilter();
    // await this.dashboardDetail()
  },
}
</script>

<style scoped lang="scss">
.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: linear-gradient(90deg, #2b3a42 0%, #4f8a8b 100%);
  border-radius: 12px;
  padding: 18px 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
}
.dashboard-header h2 {
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 1px;
  text-shadow: 0 2px 8px rgba(44, 62, 80, 0.1);
}
.title-font-size {
  font-size: 1.25rem;
}
.card-font-size {
  font-size: 1.5rem;
}
.marginTopCard {
  margin-top: 50px;
}
</style>

// selectFilter(value) { // this.selectedFilter = value // const today = new
Date() // let fromDate = null // let toDate = today // switch (value) { // case
'today': // fromDate = toDate // break // case 'yesterday': // fromDate = new
Date(today) // fromDate.setDate(today.getDate() - 1) // toDate = fromDate //
break // case '1week': // fromDate = new Date(today) //
fromDate.setDate(today.getDate() - 7) // break // case '1month': // fromDate =
new Date(today) // fromDate.setMonth(today.getMonth() - 1) // break // case
'2months': // fromDate = new Date(today) // fromDate.setMonth(today.getMonth() -
2) // break // case '3months': // fromDate = new Date(today) //
fromDate.setMonth(today.getMonth() - 3) // break // case 'custom': // // User
selects manually via datepickers // return // default: // fromDate = null //
toDate = null // } // const format = (d) => (d ? d.toISOString().split('T')[0] :
null) // this.filter.from = format(fromDate) // this.filter.to = format(toDate)
// },
